// Mock data for SALAR Technologies website

export const capabilities = [
  {
    id: 0,
    title: "Data Center Thermal Resilience Assurance",
    description: "Independent professional-services assessment of power, cooling, telemetry, failover, supplier/spares, operations, and continuity risk for AWS-dependent, cloud, colocation, enterprise, and AI/GPU compute environments. SALAR's P1–P5 evidence model produces a scorecard, gap heat map, ranked risk drivers, decision trace, and mitigation backlog. Available through AWS Marketplace as a private-offer engagement.",
    icon: "thermometer",
    awsMarketplaceUrl: "https://aws.amazon.com/marketplace/pp/prodview-qva3gyfiytksu",
    techBriefUrl: "/documents/SALAR_Thermal_Resilience_Assurance_Generic_Scenario_Brief.pdf",
    flagship: true,
    disclaimer: "No Amazon, AWS, or cloud-provider endorsement is implied."
  },
  {
    id: 1,
    title: "Design Reliability Analysis",
    description: "Structured, engineering-centered review logic for board-level reliability, design discipline, and qualification-aware evaluation.",
    icon: "cpu"
  },
  {
    id: 2,
    title: "Predictive Failure Analytics",
    description: "Explainable analytics approaches that help estimate failure risk, maintenance priority, and readiness impact across electronics systems.",
    icon: "activity"
  },
  {
    id: 3,
    title: "Semiconductor Supply-Chain Risk Intelligence",
    description: "Graph-oriented and evidence-based approaches for identifying lower-tier dependencies, concentration risk, provenance exposure, and mitigation paths.",
    icon: "network"
  },
  {
    id: 4,
    title: "Closed-Loop Manufacturing Stability",
    description: "Technical framing for drift detection, process-stability awareness, and quality-preserving operational control.",
    icon: "repeat"
  },
  {
    id: 5,
    title: "Public-Safe Process Assurance Positioning",
    description: "High-level, public-safe positioning around trusted process control and lithography/process assurance without disclosing sensitive implementation detail.",
    icon: "shield-check"
  },
  {
    id: 6,
    title: "Quantum-Safe Provenance",
    description: "Bridge legacy classical attestation into post-quantum-verifiable receipts for assets that cannot be modified — for air-gapped, defense, critical-infrastructure, semiconductor, and long-lived archive environments. Filed U.S. Utility Nonprovisional Application No. 19/686,634; Patent Pending.",
    icon: "shield-check"
  },
  {
    id: 7,
    title: "AI Agent Governance & Evidence Trace Assessment",
    description: "Evidence-backed governance for agentic decisions, operational approvals, action settlement, audit trails, rollback paths, and risk-managed automation. Assesses whether AI-agent actions are traceable, governed, evidence-backed, reversible, auditable, and settlement-safe. Aligned with SALAR's patent-pending work in agentic action governance.",
    icon: "shield-check",
    additional: true
  },
  {
    id: 8,
    title: "Supply-Chain Provenance & Resilience Intelligence Assessment",
    description: "Graph-based supplier, spares, source-confidence, counterfeit/provenance, concentration, and recovery-path risk assessment for critical electronics and infrastructure. Maps supplier, spares, component, firmware, source-confidence, and recovery-path risks into evidence-backed mitigation backlogs.",
    icon: "network",
    additional: true
  },
  {
    id: 9,
    title: "Board / Power Electronics Reliability Readiness Assessment",
    description: "Board-level thermal, electrical, derating, alternate-source, regulator, power-discrete, and component reliability readiness review. Identifies board-level and power-electronics weaknesses that can affect data-center hardware, rack/server electronics, AI infrastructure, and mission-critical equipment.",
    icon: "cpu",
    additional: true
  }
];

export const patents = [
  {
    id: 1,
    title: "Board Design Reliability Analysis Checklist Guidelines",
    category: "Design Reliability"
  },
  {
    id: 2,
    title: "Profile-Based Ethernet Connectivity for Multi-Lane SoC Devices",
    category: "Connectivity Architecture"
  },
  {
    id: 3,
    title: "Graph-Based Multi-Tier Semiconductor Supply-Chain Risk Scoring and Closed-Loop Mitigation",
    category: "Supply-Chain Intelligence"
  },
  {
    id: 4,
    title: "Closed-Loop Manufacturing Stability Systems and Methods for Multi-Domain Control Production",
    category: "Manufacturing Stability"
  },
  {
    id: 5,
    title: "AI-Certified Lithography Control",
    category: "Process Assurance",
    note: "(public-safe, high-level only)"
  },
  {
    id: 6,
    title: "Reliability-Aware Power and Thermal Orchestration Systems and Methods for High-Density AI Computing Infrastructure",
    category: "AI Infrastructure Orchestration",
    applicationNumber: "U.S. Patent Application No. 19/685,128",
    status: "Filed; Patent Pending",
    filedDate: "May 22, 2026",
    licensingPath: "/licensing/patent-6-ai-infrastructure"
  },
  {
    id: 7,
    title: "Quantum-Safe Provenance / Cryptographic Provenance Bridging",
    category: "Post-Quantum Cryptography & Trust Provenance",
    applicationNumber: "U.S. Patent Application No. 19/686,634",
    status: "Filed; Patent Pending — U.S. Utility Nonprovisional",
    filedDate: "May 23, 2026",
    licensingPath: "/licensing/patent-7-pq-provenance",
    tags: ["Post-Quantum Cryptography", "Provenance", "Defense Supply Chain", "Air-Gapped Systems", "Legacy Trust"]
  },
  {
    id: 8,
    title: "Agentic Action Settlement Governor",
    category: "Agentic AI Safety & Governance",
    applicationNumber: "U.S. Patent Application No. 64/085,045",
    status: "Filed; Patent Pending",
    filedDate: "June 5, 2026",
    techBriefUrl: "/documents/SALAR_Patent8_TechBrief.pdf",
    tags: ["Agentic AI", "AI Safety", "Reversible Settlement", "Circuit Breaker", "Multi-Agent Risk", "Settlement Proof"]
  }
];

export const documents = [
{
  "id": "portland-at-a-crossroads-2026",
  "title": "Portland at a Crossroads — Complete Public-Interest Study",
  "description": "Independent civic research by Vikrant Salar: Portland governance, public records, oversight, homelessness services, tax-policy options, and a proposed 365-day operating plan. Free full report with attribution.",
  "fileType": "PDF",
  "size": "254 pages · 6.85 MB",
  "url": "/documents/Portland_at_a_Crossroads_SALAR_2026.pdf",
  "featured": true
},
  {
    "id": 1030,
    "title": "Choosing AI That Lasts — 15-Page Public White Paper",
    "description": "Edition 3: global provider atlas, named published benchmarks, engineering calculations, full task cost and continuity. 15-page public draft; full 156-page study available on request.",
    "fileType": "PDF",
    "size": "15 pages",
    "url": "/documents/SALAR_Choosing_AI_That_Lasts_Public_White_Paper_15pp.pdf",
    "featured": true
},
  {
    "id": 1022,
    "title": "Oregon — Nature, Science & Trustworthy Evidence",
    "description": "Free regional 15-page brief: conservation context, proposed engineering contribution, architecture, measurement and Fourier methods. Full 70-page study available on request.",
    "fileType": "PDF",
    "size": "784 KB",
    "url": "/documents/SALAR_oregon_Public_Brief_15pp_Rev3.pdf"
},
  {
    "id": 1023,
    "title": "Washington — Nature, Science & Trustworthy Evidence",
    "description": "Free regional 15-page brief: conservation context, proposed engineering contribution, architecture, measurement and Fourier methods. Full 70-page study available on request.",
    "fileType": "PDF",
    "size": "650 KB",
    "url": "/documents/SALAR_washington_Public_Brief_15pp_Rev3.pdf"
},
  {
    "id": 1024,
    "title": "California — Nature, Science & Trustworthy Evidence",
    "description": "Free regional 15-page brief: conservation context, proposed engineering contribution, architecture, measurement and Fourier methods. Full 70-page study available on request.",
    "fileType": "PDF",
    "size": "697 KB",
    "url": "/documents/SALAR_california_Public_Brief_15pp_Rev3.pdf"
},
  {
    "id": 1025,
    "title": "Idaho — Nature, Science & Trustworthy Evidence",
    "description": "Free regional 15-page brief: conservation context, proposed engineering contribution, architecture, measurement and Fourier methods. Full 70-page study available on request.",
    "fileType": "PDF",
    "size": "772 KB",
    "url": "/documents/SALAR_idaho_Public_Brief_15pp_Rev3.pdf"
},
  {
    "id": 1026,
    "title": "Montana — Nature, Science & Trustworthy Evidence",
    "description": "Free regional 15-page brief: conservation context, proposed engineering contribution, architecture, measurement and Fourier methods. Full 70-page study available on request.",
    "fileType": "PDF",
    "size": "748 KB",
    "url": "/documents/SALAR_montana_Public_Brief_15pp_Rev3.pdf"
},
  {
    "id": 1027,
    "title": "Alaska — Nature, Science & Trustworthy Evidence",
    "description": "Free regional 15-page brief: conservation context, proposed engineering contribution, architecture, measurement and Fourier methods. Full 70-page study available on request.",
    "fileType": "PDF",
    "size": "640 KB",
    "url": "/documents/SALAR_alaska_Public_Brief_15pp_Rev3.pdf"
},
  {
    "id": 1028,
    "title": "British Columbia — Nature, Science & Trustworthy Evidence",
    "description": "Free regional 15-page brief: conservation context, proposed engineering contribution, architecture, measurement and Fourier methods. Full 70-page study available on request.",
    "fileType": "PDF",
    "size": "631 KB",
    "url": "/documents/SALAR_british_columbia_Public_Brief_15pp_Rev3.pdf"
},
  {
    "id": 1029,
    "title": "United States Federal — Nature, Science & Trustworthy Evidence",
    "description": "Free regional 15-page brief: conservation context, proposed engineering contribution, architecture, measurement and Fourier methods. Full 70-page study available on request.",
    "fileType": "PDF",
    "size": "669 KB",
    "url": "/documents/SALAR_united_states_federal_Public_Brief_15pp_Rev3.pdf"
},
  {
    id: 1001,
    title: "Type 2 Diabetes — Public Research Brief",
    description: "Revised two-page research brief on testing whether biological measurements improve prediction of durable remission beyond simpler clinical measures. No clinical efficacy is claimed.",
    fileType: "PDF",
    size: "802 KB",
    url: "/documents/SALAR_Type_2_Diabetes_Public_Brief_Rev2.pdf"
  },
  {
    id: 1002,
    title: "HIV-1 & HIV-2 — Public Research Brief",
    description: "Revised two-page research brief on testing a type-specific reservoir prediction model against existing assays. No cure or clinical efficacy is claimed.",
    fileType: "PDF",
    size: "725 KB",
    url: "/documents/SALAR_HIV1_HIV2_Public_Brief_Rev2.pdf"
  },
  {
    id: 0,
    title: "Data Center Thermal Resilience Assurance — Scenario Brief",
    description: "Public-safe scenario brief showing how SALAR maps thermal or power events into a P1–P5 evidence model, scorecard, heat map, mitigation backlog, buyer routes, and commercial assessment path. Aligned to the AWS Marketplace professional-services listing for SALAR Thermal / Cloud Resilience Assurance Assessment. No Amazon, AWS, or cloud-provider endorsement is implied.",
    fileType: "PDF",
    size: "1.0 MB",
    url: "/documents/SALAR_Thermal_Resilience_Assurance_Generic_Scenario_Brief.pdf",
    featured: true,
    awsMarketplaceUrl: "https://aws.amazon.com/marketplace/pp/prodview-qva3gyfiytksu"
  },
  {
    id: 1,
    title: "Government Capability Statement",
    description: "Comprehensive overview of SALAR's technical capabilities and engineering focus areas for government and defense applications.",
    fileType: "PDF",
    size: "156.7 KB",
    url: "https://customer-assets.emergentagent.com/job_salar-web-app/artifacts/i1hsfwx8_SALAR_Government_Capability_Statement.pdf"
  },
  {
    id: 2,
    title: "SALAR Technologies Quad Chart",
    description: "Visual overview of SALAR's core capabilities, technical focus areas, and value proposition for defense and government applications.",
    fileType: "PDF",
    size: "TBD",
    url: "https://customer-assets.emergentagent.com/job_salar-web-app/artifacts/ligr72eb_SALAR_TECH_Quad_Chart_Website.pdf"
  },
  {
    id: 3,
    title: "SALAR Tech Introduction",
    description: "Complete introduction to SALAR Technologies' mission, capabilities, and strategic positioning in defense electronics.",
    fileType: "PDF",
    size: "TBD",
    url: "https://customer-assets.emergentagent.com/job_salar-web-app/artifacts/b250um4u_SALAR%20Tech%20Intro.pdf"
  },
  {
    id: 4,
    title: "Technical Brief — Counterfeit Avoidance & Provenance Intelligence",
    description: "One-page external brief for DLA routing, CDAP discussions, and mission-electronics sustainment engagement. Covers provenance scoring, graph-based dependency mapping, and board-level reliability review.",
    fileType: "PDF",
    size: "6.7 KB",
    url: "/SALAR_OnePage_Tech_Brief.pdf"
  },
  {
    id: 5,
    title: "Public Patent Portfolio Overview",
    description: "Filed and public-safe technology lanes relevant to advanced electronics reliability, semiconductor systems, supply-chain assurance, AI infrastructure orchestration, post-quantum trust provenance, and agentic-AI safety governance. All 8 USPTO applications summarized.",
    fileType: "PDF",
    size: "148 KB",
    url: "/SALAR_Public_Patent_Overview.pdf"
  },
  {
    id: 6,
    title: "Company Line Card",
    description: "Small-business summary with corporate identifiers, core offering lines, patent-backed lanes, best-fit customer use cases, and representative deliverables.",
    fileType: "PDF",
    size: "147 KB",
    url: "/SALAR_Company_Line_Card.pdf"
  },
  {
    id: 7,
    title: "Corporate Data Sheet & Patent Summary",
    description: "Complete corporate data including UEI, CAGE, EIN, NAICS codes, SBIR control ID, core mission areas, and public-safe patent portfolio. For DLA / MIB / CDAP routing.",
    fileType: "PDF",
    size: "144 KB",
    url: "/SALAR_Corporate_Data_Sheet.pdf"
  },
  {
    id: 8,
    title: "SBIR Performance Benchmark Applicability Report",
    description: "Applicability analysis of SALAR's patent-backed capability lanes against SBIR performance benchmarks, covering reliability, supply-chain risk analytics, manufacturing stability, and process assurance alignment with DoD / SBIR priorities.",
    fileType: "PDF",
    size: "44 KB",
    url: "/SALAR_SBIR_Performance_Benchmark_Report.pdf"
  }
];

export const complianceDocs = [
  {
    id: 1,
    title: "Compliance & Certification Stack — Master Overview",
    intro: "SALAR Technologies maintains a layered compliance posture aligned to U.S. federal contracting requirements. This master overview consolidates the company's certification stack, governance practices, and cybersecurity readiness across DoD, GSA, and federal civilian engagement pathways. It is the recommended first read for prime contractors, contracting officers, and program offices conducting supplier vetting.",
    fileType: "PDF",
    size: "218 KB",
    url: "/SALAR_Compliance_Certification_Stack.pdf"
  },
  {
    id: 2,
    title: "CMMC Level 1 — FCI Self-Assessment Posture",
    intro: "Cybersecurity Maturity Model Certification (CMMC) Level 1 self-assessment covering all 17 basic safeguarding practices required for handling Federal Contract Information (FCI). Documents SALAR's compliance posture against FAR 52.204-21 and serves as a supporting artifact for DoD prime/subcontract eligibility, supplier on-boarding, and pre-award due diligence.",
    fileType: "PDF",
    size: "18 KB",
    url: "/SALAR_CMMC_Level1_FCI_Self_Assessment.pdf"
  },
  {
    id: 3,
    title: "AI Use & Engineering Governance Addendum",
    intro: "Governance addendum describing SALAR's responsible-use policies for AI and machine-learning tools across engineering, analytics, and research workflows. Defines human-in-the-loop review boundaries, data-handling controls, model-output validation discipline, and audit trails — aligned to emerging federal AI procurement and risk-management expectations.",
    fileType: "PDF",
    size: "18 KB",
    url: "/SALAR_AI_Engineering_Governance_Addendum.pdf"
  },
  {
    id: 4,
    title: "FAR 52.204-21 — FCI Baseline Cybersecurity Readiness",
    intro: "Detailed readiness mapping against FAR 52.204-21 'Basic Safeguarding of Covered Contractor Information Systems.' Each of the 15 basic safeguarding requirements is documented with SALAR's implementation evidence, control owner, and review cadence — providing a clean audit trail for contracting officers and prime contractors.",
    fileType: "PDF",
    size: "18 KB",
    url: "/SALAR_FAR_52_204_21_Cybersecurity_Readiness.pdf"
  },
  {
    id: 5,
    title: "Prime / Subcontractor Target Pack",
    intro: "Prepared engagement pack for prime contractors evaluating SALAR as a teaming partner or specialty subcontractor. Includes capability-lane mapping, patent-backed differentiation, NAICS coverage, registration credentials (UEI, CAGE, DUNS), and the public-safe portfolio summary needed for prime-contractor supplier diligence and teaming agreements.",
    fileType: "PDF",
    size: "41 KB",
    url: "/SALAR_Prime_Subcontractor_Target_Pack.pdf"
  },
  {
    id: 6,
    title: "Website Upload Index — Trust & Compliance",
    intro: "Master index of every trust, compliance, and certification artifact published on this website. Cross-references each document's purpose, audience, control owner, and revision date — providing prime contractors and contracting officers a single reference sheet for SALAR's public compliance footprint and document trail.",
    fileType: "PDF",
    size: "84 KB",
    url: "/SALAR_Website_Upload_Index_Trust_Compliance.pdf"
  }
];

export const publications = [
  {
    id: 1,
    title: "Rare Earth Trap - Long Brief",
    description: "Detailed analysis of rare earth supply chain vulnerabilities and strategic implications.",
    fileType: "PDF",
    size: "181.2 KB",
    url: "https://customer-assets.emergentagent.com/job_salar-web-app/artifacts/vajqvv8b_SALAR_book1_rare_earth_trap_Long_Brief.pdf",
    category: "Book 1"
  },
  {
    id: 2,
    title: "Rare Earth Trap - Short Brief",
    description: "Executive summary of rare earth supply chain risks and mitigation strategies.",
    fileType: "PDF",
    size: "177.9 KB",
    url: "https://customer-assets.emergentagent.com/job_salar-web-app/artifacts/nzl8ee6g_SALAR_book1_rare_earth_trap_Short_Brief.pdf",
    category: "Book 1"
  },
  {
    id: 3,
    title: "Counterfeit Menace - Long Brief",
    description: "In-depth analysis of counterfeit electronics threats and detection methodologies.",
    fileType: "PDF",
    size: "181.1 KB",
    url: "https://customer-assets.emergentagent.com/job_salar-web-app/artifacts/f876sjo5_SALAR_book2_counterfeit_menace_Long_Brief.pdf",
    category: "Book 2"
  },
  {
    id: 4,
    title: "Counterfeit Menace - Short Brief",
    description: "Concise overview of counterfeit risks in defense electronics supply chains.",
    fileType: "PDF",
    size: "177.9 KB",
    url: "https://customer-assets.emergentagent.com/job_salar-web-app/artifacts/ci2czk5s_SALAR_book2_counterfeit_menace_Short_Brief.pdf",
    category: "Book 2"
  },
  {
    id: 5,
    title: "Manufacturing Sovereignty - Short Brief",
    description: "Strategic analysis of manufacturing independence and resilience in defense electronics.",
    fileType: "PDF",
    size: "TBD",
    url: "https://customer-assets.emergentagent.com/job_salar-web-app/artifacts/4hoxb95e_SALAR_book3_manufacturing_sovereignty_Short_Brief.pdf",
    category: "Book 3"
  },
  {
    id: 6,
    title: "Invisible Supply Chain - Long Brief",
    description: "Comprehensive examination of hidden supply chain vulnerabilities and risks.",
    fileType: "PDF",
    size: "TBD",
    url: "https://customer-assets.emergentagent.com/job_salar-web-app/artifacts/h1ukbayo_SALAR_book4_invisible_supply_chain_Long_Brief.pdf",
    category: "Book 4"
  },
  {
    id: 7,
    title: "Invisible Supply Chain - Short Brief",
    description: "Executive overview of supply chain opacity and mitigation strategies.",
    fileType: "PDF",
    size: "TBD",
    url: "https://customer-assets.emergentagent.com/job_salar-web-app/artifacts/pcdq6clx_SALAR_book4_invisible_supply_chain_Short_Brief.pdf",
    category: "Book 4"
  },
  {
    id: 8,
    title: "Silicon Governance - Long Brief",
    description: "In-depth analysis of semiconductor governance, policy, and strategic control.",
    fileType: "PDF",
    size: "TBD",
    url: "https://customer-assets.emergentagent.com/job_salar-web-app/artifacts/n3zhpvf0_SALAR_book5_silicon_governance_Long_Brief.pdf",
    category: "Book 5"
  },
  {
    id: 9,
    title: "Silicon Governance - Short Brief",
    description: "Concise examination of silicon supply chain governance and control mechanisms.",
    fileType: "PDF",
    size: "TBD",
    url: "https://customer-assets.emergentagent.com/job_salar-web-app/artifacts/bhs1khn8_SALAR_book5_silicon_governance_Short_Brief.pdf",
    category: "Book 5"
  },
  {
    id: 10,
    title: "American Arsenal - Long Brief",
    description: "Comprehensive analysis of U.S. defense electronics supply-chain sovereignty.",
    fileType: "PDF",
    size: "TBD",
    url: "https://customer-assets.emergentagent.com/job_salar-web-app/artifacts/yv6t3zh3_SALAR_book6_american_arsenal_Long_Brief.pdf",
    category: "Book 6"
  },
  {
    id: 11,
    title: "American Arsenal - Short Brief",
    description: "Executive overview of defense electronics sovereignty and strategic positioning.",
    fileType: "PDF",
    size: "TBD",
    url: "https://customer-assets.emergentagent.com/job_salar-web-app/artifacts/i2smmh35_SALAR_book6_american_arsenal_Short_Brief.pdf",
    category: "Book 6"
  }
];

export const updates = [
{
  "id": "gpmi-one-cable-patent-11-2026-10-28",
  "title": "SALAR Files Patent 11 and Introduces GPMI One Cable",
  "date": "October 28, 2026",
  "category": "Patent Filing / Product Launch",
  "image": "/img/pexels_fiber_cables.jpg",
  "imageAlt": "High-speed cable and interconnect imagery representing SALAR GPMI One Cable engineering development",
  "link": "/products/gpmi-one-cable",
  "targetType": "page",
  "targetUrl": "/products/gpmi-one-cable",
  "ctaLabel": "Explore GPMI One Cable",
  "badgeLabel": "Patent 11 / New Product",
  "tags": ["Patent 11", "GPMI One Cable", "Data + Power", "Interconnect", "Signal Integrity", "Hardware Trust", "SALAR Technologies"],
  "excerpt": "SALAR Technologies LLC is excited to share its 11th U.S. patent filing and a new physical-product direction: GPMI One Cable, a patent-pending unified data and power cable architecture with plug-resident signed channel identity, physical-channel fingerprint verification, and sequenced power enablement.",
  "body": "SALAR Technologies LLC is excited to share the filing of its 11th U.S. patent application and the introduction of GPMI One Cable, a new physical interconnect product direction. U.S. Provisional Application No. 64/185,728, filed October 27, 2026, is titled “Unified Data and Power Cable Assembly with Plug-Resident Signed Channel Record, Impedance Fingerprint Verification, and Sequenced Power Enablement.”\n\nThe public product concept connects three ideas in one cable architecture: a signed record associated with the physical assembly, verification of physical channel or impedance fingerprint evidence, and a controlled sequence for enabling power. The goal is to make the cable itself part of the trust and safety boundary rather than treating it as an anonymous passive conduit.\n\nGPMI One Cable is an engineering-development product, not a released production datasheet. Final data-rate, power-delivery, connector, reach, BER/FEC, thermal, safety, interoperability and compliance claims remain validation-gated. GPMI is an industry interface reference; SALAR does not claim GPMI certification, consortium endorsement or wire-level compatibility unless separately established."
},
{
  "id": "portland-at-a-crossroads-2026-10-26",
  "title": "Our Commitment to Society: Portland at a Crossroads",
  "date": "October 26, 2026",
  "category": "Public-Interest Research / Community",
  "image": "/images/community/portland-waterfront-illustration.webp",
  "imageAlt": "AI-generated illustration inspired by Portland’s Willamette River waterfront; not documentary evidence.",
  "link": "/commitment-to-society.html",
  "targetType": "page",
  "targetUrl": "/commitment-to-society.html",
  "ctaLabel": "Explore the study and download the report",
  "badgeLabel": "Free Full Report",
  "openInNewTab": true,
  "downloadUrl": "/documents/Portland_at_a_Crossroads_SALAR_2026.pdf",
  "downloadLabel": "Download the complete 254-page report",
  "excerpt": "SALAR Technologies LLC shares Portland at a Crossroads, a free 254-page public-interest study by Vikrant Salar. Explore the evidence, proposed civic reforms, and a 30/90/180/365-day improvement roadmap on our new Commitment to Society page.",
  "body": "SALAR Technologies LLC’s commitment to society begins with making useful research accessible. Portland at a Crossroads brings together public audits, government data, regional reports, and published reporting to examine how Portland and its region can deliver more accountable services.\n\nWritten by Vikrant Salar, Founder & Principal, the October 2026 study contains 30 chapters across seven parts. It examines governance, public records, independent oversight, homelessness services, talent, tax-policy options, and responsible technology. Its proposed operating plan connects clear ownership and published baselines to measurable milestones at 30, 90, 180, and 365 days.\n\nThe full 254-page PDF is available free, with no registration. Readers may share it in full with attribution. The new page provides a short reading guide, source-linked findings, proposed fixes, and a shareable stakeholder overview. Reported evidence, the author’s interpretations, and SALAR estimates are distinguished; the plan is a proposal, not an adopted City program.\n\nResidents, public-service teams, businesses, and researchers are invited to review the sources and submit corrections with supporting evidence to contact@salartechnologies.com. The waterfront and community images are AI-generated illustrations."
},
{
    "id": "choosing-ai-that-lasts-2026-10-26",
    "title": "Choosing AI That Lasts - Revised Global Study, Edition 3",
    "date": "October 26, 2026",
    "category": "Research / Publication",
    "image": "/images/ai-in-contest/engineering-research.jpg",
    "imageAlt": "NIST engineer Richard Kasica with a silicon wafer beside fabrication equipment. Photo: Curt Suplee/NIST.",
    "link": "/ai-in-contest.html",
    "targetType": "page",
    "targetUrl": "/ai-in-contest.html",
    "ctaLabel": "Explore the research",
    "badgeLabel": "Resource",
    "openInNewTab": true,
    "downloadUrl": "/documents/SALAR_Choosing_AI_That_Lasts_Public_White_Paper_15pp.pdf",
    "downloadLabel": "Download the 15-page white paper",
    "excerpt": "Global AI selection with named benchmark results, a 46-entry regional atlas, 34 figures, 55 equations and six worked cases. Download the revised 15-page public draft; request the full 156-page study.",
    "body": "SALAR Technologies LLC publishes edition 3 of Choosing AI That Lasts: Global Models, Measured Capability, Cost, Risk and Survival, 2026-2031, by Vikrant Salar, Founder & Principal.\n\nThe 156-page research draft compares published coding, factuality, grounding and tool-use evidence for named systems. A 46-entry regional atlas looks beyond US providers to China, India, Europe, Britain, Russia, Israel, Japan, Korea, Southeast Asia, Australia, New Zealand, Latin America and the Arab world. The study distinguishes developers, models, applications and deployment contracts.\n\nThe edition contains 34 figures, 55 equations, 183 cited sources, real documentary photographs and six worked cases. It includes original reanalysis of published task outcomes, actual MLPerf system results, manufacturer-rated hardware constraints, dated API rate calculations and clearly labeled hypothetical scenarios. SALAR did not run a new proprietary-model benchmark.\n\nThe 15-page public draft selects nine figures from the full edition. Both documents have their bibliography at the end. Read the paper and one-page summary from the research page. For the full study or a tailored evaluation, contact vikrant@salartechnologies.com or contact@salartechnologies.com."
},
  {
    "id": 41,
    "title": "Nature, Science & Trustworthy Evidence: Regional Studies, Revision 3",
    "date": "October 22, 2026",
    "category": "Independent Research / Conservation Engineering",
    "image": "/images/conservation/rev3/oregon.jpg",
    "imageAlt": "Generated illustration of an Oregon oak and basalt river landscape; not a field photograph",
    "link": "/environmental-research",
    "targetType": "page",
    "targetUrl": "/environmental-research",
    "ctaLabel": "Explore the regional studies",
    "badgeLabel": "Research Publication",
    "tags": [
        "Conservation",
        "Biodiversity",
        "Scientific Data",
        "Fourier Analysis",
        "Systems Engineering"
    ],
    "excerpt": "Eight regional conservation studies now include distinct habitat imagery and regional design plates, with an interactive technical-figure library. Free 15-page briefs are available for each jurisdiction; full 70-page studies are available on request.",
    "body": "Conservation decisions increasingly depend on a chain of instruments, field observations, maps, laboratory records, software and review. SALAR Technologies LLC’s independent research asks how that chain can retain the methods, effort, uncertainty and permissions needed for trustworthy decisions.\n\nEight regional editions cover Oregon, Washington, California, Idaho, Montana, Alaska, British Columbia and United States federal programs. Each has its own free 15-page public brief and a complete 70-page study available through the research-page request form. The studies connect regional habitat designs with system architectures, observation relationships, instrument acquisition, Fourier analysis, scientific release decisions, provenance and offline field workflows. Large vector figures support detailed inspection. Each jurisdiction has its own generated habitat illustration and a proposed regional integration design.\n\nThe research examines sensor physics, measurement uncertainty, imperfect detection, Fourier transforms and acoustic review, environmental DNA, remote sensing, data contracts, provenance, controlled disclosure and recovery. Regional chapters distinguish existing agency capabilities from proposed assessment and pilot work. Commercial discussion focuses on a defined service with measurable acceptance, rather than resale of public facts.\n\nThe purpose is to support stewardship of biodiversity and ecological function for future generations. This is independent public-source analysis and proposed engineering design, not commissioned agency work, original field validation or externally peer-reviewed findings. No purchasing commitment, guaranteed outcome or matching open procurement is claimed. British Columbia requires supplier-eligibility review under current restrictions affecting U.S. companies. The landscape image is generated illustration, not survey evidence."
},
  {
    id: 40,
    title: "SALAR Publishes After the End of Life v3.0: Preserving Legacy Embedded Architectures",
    date: "October 21, 2026",
    category: "Research Publication / Legacy Sustainment and DMSMS",
    image: "/img/legacy-eol/hero-legacy-board.webp",
    imageAlt: "Photorealistic render of a legacy 6U VME single board computer used in long-life defense and infrastructure systems",
    link: "/after-the-end-of-life",
    targetType: "page",
    targetUrl: "/after-the-end-of-life",
    ctaLabel: "Read the study and download the public edition",
    badgeLabel: "New Research",
    tags: ["Legacy Sustainment", "DMSMS", "Obsolescence", "Counterfeit Risk", "VME", "Defense Electronics"],
    excerpt: "Founder and Principal Vikrant Salar publishes version 3.0 of his field study on keeping long-life systems running after their electronics leave production. Public DMSMS cost data shows a system redesign averages about $10.3 million against about $1,028 for an approved-part fix, and that about 97% of modeled resolution spend sits in a small group of expensive outcomes. The study models keeping a qualified design alive at about one third of the cost of redesign, maps the rules across defense, aviation, rail, grid and medical sectors, and proposes a six-engine Legacy Preservation Architecture. A free 16-page public edition is available now. The full 101-page research edition is available on request.",
    body: "Ships, aircraft, rail networks, power grids and hospital imaging systems are certified to serve for thirty to fifty years. The processors, memories and bus bridges inside them stay in production for five to twelve. After the End of Life v3.0 examines what happens in that gap. It draws on the public DMSMS Cost Metrics report, DoD Instruction 4245.15, the SD-22 guidebook, the 2012 Senate Armed Services Committee counterfeit inquiry, GAO-12-375, sector standards from the FAA, FDA, NERC, CENELEC and IEC, and SALAR's own research on counterfeit electronics, supply-chain choke points and manufacturing stability. Version 3.0 adds six sector chapters, a regulatory map checked in October 2026, a behavioral equivalence method for re-implemented boards, a maturity model, a commercial chapter on how sustainment is priced, three new scenarios, and Monte Carlo and sensitivity analysis. The study finds that the sustainment market's core model, licensed takeover of end-of-life designs, is sound but reactive and catalog-centric, and that programs pay for that uncertainty through setup charges, expedite fees and last-time buys. It proposes one digital thread vault and six engines: Knowledge Capture, Test Twin, Component Risk Graph, Provenance Ledger, Stability Engine and Fleet Demand Model. SALAR Technologies applies these methods through fixed-fee portfolio risk assessments, component risk monitoring, Test Twin and behavioral equivalence work, fleet demand modeling and sustainment program office setup."
  },
  {
    id: 39,
    title: "SALAR Updates Medical Research Perspectives and Invites Validation Partners",
    date: "September 27, 2026",
    category: "Independent Research / Medical Science",
    image: "/img/medical-science/diabetes-organ-system-rev2.webp",
    imageAlt: "Conceptual illustration of organs involved in metabolic research",
    link: "/engineering-advancement-medical-science",
    targetType: "page",
    targetUrl: "/engineering-advancement-medical-science",
    ctaLabel: "Explore the Research Directions",
    badgeLabel: "Research Direction",
    tags: ["Medical Science", "Type 2 Diabetes", "HIV-1", "HIV-2", "Research Collaboration"],
    excerpt: "SALAR has revised its independent diabetes and HIV research perspectives around published evidence, explicit comparison studies, and failure criteria. Two updated public briefs invite scientific review; SALAR has not conducted clinical trials or demonstrated a treatment or cure.",
    body: "The updated Engineering Advancement in Medical Science page presents two independent research perspectives and two revised public briefs. The diabetes proposal asks whether early stimulated C-peptide and organ-fat measures improve 12- and 24-month remission prediction over inexpensive clinical measures. The HIV proposal asks whether a type-specific, uncertainty-aware reservoir model improves prediction over existing blood assays, with separate HIV-2 validation. The published biological mechanisms and trial findings are credited to prior investigators. SALAR has not conducted a clinical trial, established a therapy, or demonstrated a cure. The full 20-page perspectives are retained privately for research collaboration."
  },
  {
    id: 38,
    title: "SALAR Publishes AI Sovereignty and Model Centralization",
    date: "August 29, 2026",
    category: "White Paper / Strategic Research",
    image: "/img/news/salar-ai-sovereignty-map-2026.webp",
    imageAlt: "SALAR AI Dominance Map 2026 showing the global contest over models, compute, chips, energy, and policy",
    link: "/ai-sovereignty-report",
    targetType: "page",
    targetUrl: "/ai-sovereignty-report",
    ctaLabel: "Read the Executive Brief",
    badgeLabel: "New White Paper",
    downloadUrl: "/documents/SALAR_AI_Sovereignty_Executive_Brief_WP_2026_006.pdf",
    downloadLabel: "Download 3-Page Brief",
    tags: ["AI Sovereignty", "Semiconductors", "AI Infrastructure", "Geopolitics", "Taiwan", "SALAR-WP-2026-006"],
    excerpt: "SALAR Technologies LLC has published a new three-page executive brief and a controlled-access 26-page report mapping who controls AI and the semiconductor stack beneath it. The analysis frames the United States as the clear leader, China as the challenger, Europe as the regulatory power, India as the wildcard, and Taiwan as the decisive physical convergence point.",
    body: "SALAR Technologies LLC has published AI Sovereignty and Model Centralization, SALAR-WP-2026-006. The work argues that AI leadership is a coupled contest over frontier intelligence, accelerator architecture, lithography, foundry and packaging capacity, HBM, cloud infrastructure, power, and policy. A public three-page executive brief presents two world-atlas maps, a 55% / 35% / 10% 2031 scenario frame, and a practical definition of sovereignty as controlled dependence with credible exit options. The full 26-page publication edition is available through a password-controlled portal for qualified policy, research, national-security, semiconductor, infrastructure, investment, and media readers."
  },
  {
    id: 37,
    title: "SALAR Opens Strategic Partner and Investor Relations Path With Eight Patent Areas",
    date: "June 11, 2026",
    category: "Company Update",
    image: "/img/salar_hero_semiconductor_resilience.webp",
    imageAlt: "Semiconductor wafer and electronic system imagery representing SALAR Technologies strategic investor and partner inquiry path.",
    link: "/strategic-partners",
    targetType: "page",
    targetUrl: "/strategic-partners",
    ctaLabel: "View Strategic Partners Page",
    badgeLabel: "Page",
    tags: ["Strategic Partners", "Investor Relations", "Eight Patent Areas", "Electronics Resilience", "AI Infrastructure Assurance", "SALAR Technologies"],
    excerpt: "SALAR Technologies LLC has opened a public Strategic Partners & Investor Relations path for qualified investors, corporate partners, pilot customers, grant partners, licensing partners, and technology-transition organizations. The updated positioning reflects eight public USPTO patent filings spanning electronics reliability, semiconductor supply-chain intelligence, manufacturing stability, AI infrastructure assurance, quantum-safe provenance, and accountable agentic decision governance.",
    body: "SALAR Technologies LLC has opened a public Strategic Partners & Investor Relations path for qualified investors, corporate partners, pilot customers, grant partners, licensing partners, and technology-transition organizations. The updated positioning reflects eight public USPTO patent filings spanning electronics reliability, semiconductor supply-chain intelligence, manufacturing stability, AI infrastructure assurance, quantum-safe provenance, and accountable agentic decision governance.\n\nThis announcement is for relationship-building and inquiry purposes only. It does not constitute an offer to sell securities or a solicitation to buy securities. Any financing, if pursued, will be conducted only through appropriate legal documentation and in compliance with applicable federal and state securities laws."
  },
  {
    id: 36,
    title: "SALAR Launches AWS Marketplace Professional-Services Listing for Thermal / Cloud Resilience Assurance",
    date: "June 8, 2026",
    category: "Service Launch",
    image: "/img/data_center_servers.jpg",
    imageAlt: "Data center server racks representing thermal cloud resilience",
    link: "https://aws.amazon.com/marketplace/pp/prodview-qva3gyfiytksu",
    targetType: "external",
    targetUrl: "https://aws.amazon.com/marketplace/pp/prodview-qva3gyfiytksu",
    ctaLabel: "View AWS Marketplace Listing",
    badgeLabel: "Service Launch",
    tags: ["AWS Marketplace", "Thermal Resilience", "Data Center", "Professional Services", "P1-P5 Model", "SALAR Technologies"],
    techBriefUrl: "/documents/SALAR_Thermal_Resilience_Assurance_Generic_Scenario_Brief.pdf",
    disclaimer: "No Amazon, AWS, or cloud-provider endorsement is implied.",
    excerpt: "SALAR Technologies LLC has established an AWS Marketplace professional-services listing for SALAR Thermal / Cloud Resilience Assurance Assessment. The service helps AWS-dependent, cloud, colocation, enterprise, data-center, and AI infrastructure teams evaluate power, thermal, telemetry, supplier/spares, failover, operations, and customer-continuity risks using SALAR's P1–P5 evidence model. The listing supports private-offer scoping for qualified buyers.",
    body: "SALAR Technologies LLC has established an AWS Marketplace professional-services listing for SALAR Thermal / Cloud Resilience Assurance Assessment. The service helps AWS-dependent, cloud, colocation, enterprise, data-center, and AI infrastructure teams evaluate power, thermal, telemetry, supplier/spares, failover, operations, and customer-continuity risks using SALAR's P1–P5 evidence model. The listing supports private-offer scoping for qualified buyers.\n\nNo Amazon, AWS, or cloud-provider endorsement is implied."
  },
  {
    id: 35,
    title: "SALAR Technologies Files Patent 8 — Agentic Action Settlement Governor",
    date: "June 5, 2026",
    category: "Patent Filing",
    image: "/img/salar_capability_trust_assurance.webp",
    imageAlt: "Trust assurance visual representing verifiable action governance",
    link: "/patent-portfolio#patent-8",
    targetType: "page",
    targetUrl: "/patent-portfolio#patent-8",
    ctaLabel: "View Patent 8",
    badgeLabel: "Patent Filing",
    tags: ["Agentic AI", "AI Safety", "Patent 8", "Settlement Governor", "Reversible Settlement", "SALAR Technologies"],
    techBriefUrl: "/documents/SALAR_Patent8_TechBrief.pdf",
    excerpt: "SALAR Technologies has filed a U.S. patent application for the Agentic Action Settlement Governor (Application No. 64/085,045), a new control layer for autonomous AI agents. The invention inserts a reversible holding stage between an agent's action and its irreversible effect, automatically reversing pending actions if combined cross-agent risk crosses a threshold — then issues a tamper-evident settlement proof for auditors.",
    body: "SALAR Technologies has filed a U.S. patent application for the Agentic Action Settlement Governor (Application No. 64/085,045), a new control layer for autonomous AI agents. As AI agents increasingly take real actions — moving money, changing systems, sending messages — two risks dominate: actions that cannot be undone, and emergent cascades where many individually-approved actions combine into serious harm.\n\nThe invention inserts a reversible holding stage between an agent's action and its irreversible effect. Each action is held for a time proportional to how risky and how irreversible it is, while a monitor tracks combined risk across all agents. If the combined risk crosses a threshold, a circuit breaker automatically reverses the pending actions before they become permanent and issues a tamper-evident settlement proof for auditors.\n\nThe approach is patent pending. Read the technology brief: /documents/SALAR_Patent8_TechBrief.pdf"
  },
  {
    id: 34,
    title: "SALAR submits to DIU Project Spectrum Strike (PROJ00678)",
    date: "June 3, 2026",
    category: "Submission / Demo",
    image: "/img/tech_closeup.jpg",
    imageAlt: "Electronic systems close-up representing RF spectrum assurance",
    link: "/spectrum-strike-demo",
    targetType: "demo",
    targetUrl: "/spectrum-strike-demo",
    ctaLabel: "Open the Live Demo",
    badgeLabel: "Demo",
    tags: ["Spectrum Strike", "DIU PROJ00678", "Live Demo", "Submission", "SALAR Technologies"],
    excerpt: "SALAR Technologies LLC has submitted its Spectrum Strike Assurance Engine to the Defense Innovation Unit's Project Spectrum Strike prize challenge (PROJ00678). The submission proposes an explainable, interagency decision layer for automated spectrum authorization. A live demonstration console is available at /spectrum-strike-demo.",
    body: "SALAR Technologies LLC has submitted its Spectrum Strike Assurance Engine to the Defense Innovation Unit's Project Spectrum Strike prize challenge (PROJ00678). The submission proposes an explainable, interagency decision layer for automated spectrum authorization. A live demonstration console is available at /spectrum-strike-demo."
  },
  {
    id: 33,
    title: "SALAR Technologies launches the full Patent Licensing portal",
    date: "May 24, 2026",
    category: "Patent Licensing",
    image: "/img/salar_hero_semiconductor_resilience.webp",
    imageAlt: "Semiconductor resilience visual representing patent licensing and technology transfer",
    link: "/licensing",
    targetType: "page",
    targetUrl: "/licensing",
    ctaLabel: "View Patent Licensing",
    badgeLabel: "Page",
    tags: ["Patent Licensing", "Post-Quantum Cryptography", "AI Infrastructure", "Defense Supply Chain", "SALAR Technologies"],
    excerpt: "SALAR Technologies LLC has launched a dedicated patent licensing portal covering all eight pending US patent applications — board reliability, multi-lane Ethernet, semiconductor supply-chain graph intelligence, manufacturing stability, AI-certified lithography control, AI infrastructure orchestration, post-quantum hardware provenance, and agentic action settlement governance. Public 1-page glossies are available per patent; the full 9–10 page Licensing Briefs are NDA-only and delivered by email after a one-page mutual NDA.",
    body: "SALAR Technologies LLC has launched its dedicated patent licensing portal at /licensing. The portal hosts public 1-page glossy summaries for each of the eight pending US utility patent applications in SALAR's portfolio, an integrated stack visualization showing how the eight inventions combine into a single verifiable-resilience platform for AI-era infrastructure, defense, commercial, industrial, and consumer electronics ecosystems, and a streamlined inquiry process. Non-exclusive, exclusive, joint-development, cross-license, and strategic-acquisition structures are available. Inquiries route to licensing@salartechnologies.com; SALAR returns a one-page mutual NDA within two business days. Full Licensing Briefs are delivered by email post-NDA and are not hosted on the public website."
  },
  {
    id: 30,
    title: "Thermal Resilience Scenario Brief Added to Total Solution Architecture",
    date: "May 12, 2026",
    category: "Website Release / Scenario Brief",
    image: "/img/pexels_server_room.jpg",
    imageAlt: "Server room visual representing thermal resilience architecture",
    link: "/total-solution-architecture",
    targetType: "page",
    targetUrl: "/total-solution-architecture",
    ctaLabel: "View Total Solution Architecture",
    badgeLabel: "Page",
    downloadUrl: "/documents/SALAR_Thermal_Resilience_Assurance_Generic_Scenario_Brief.pdf",
    downloadLabel: "Download Scenario PDF",
    excerpt: "SALAR Technologies LLC added a public-safe thermal resilience scenario brief showing how the P1-P5 assurance model can help translate a data-center thermal or power event into evidence intake, readiness scoring, mitigation sequencing, and scoped assurance work.",
    body: "SALAR Technologies LLC added a public-safe thermal resilience scenario brief showing how the P1-P5 assurance model can help translate a data-center thermal or power event into evidence intake, readiness scoring, mitigation sequencing, and scoped assurance work. The new scenario brief complements the existing 20-slide Total Solution Architecture deck. The 20-slide deck explains the full SALAR assurance model, while the scenario brief demonstrates how the model can be applied to a generic thermal or power-risk event affecting compute, cloud, colocation, or AI infrastructure environments. The material is generic and non-endorsing; it does not diagnose any private cloud provider, customer, or facility."
  },
  {
    id: 29,
    title: "PCIe 6.0 / PAM4 Link Assurance Brief Added",
    date: "May 12, 2026",
    category: "Mission Assurance / Capability Brief",
    image: "/img/news/high-speed-interconnect-fiber-cables.svg",
    imageAlt: "Fiber-optic interconnect cables representing PCIe 6.0 and PAM4 link assurance",
    link: "/mission-assurance#pcie-pam4-link-assurance",
    targetType: "page",
    targetUrl: "/mission-assurance#pcie-pam4-link-assurance",
    ctaLabel: "View Mission Assurance",
    badgeLabel: "Page",
    downloadUrl: "/documents/SALAR_PCIe6_PAM4_Link_Assurance_Website_Upgrade_Brief_2026-05-12.pptx",
    downloadLabel: "Download PCIe / PAM4 Brief",
    excerpt: "SALAR Technologies LLC added a new PCIe 6.0 / PAM4 Link Assurance brief to the Mission Assurance page. The 18-slide partner brief explains how high-speed interconnect evidence, board/package reliability, supplier provenance, manufacturing stability, and qualification readiness can be translated into a repeatable link-health score and decision record.",
    body: "SALAR Technologies LLC added a new PCIe 6.0 / PAM4 Link Assurance brief to the Mission Assurance page. The 18-slide partner brief explains how high-speed interconnect evidence, board/package reliability, supplier provenance, manufacturing stability, and qualification readiness can be translated into a repeatable link-health score and decision record. SALAR does not claim to be a PCIe compliance lab, PHY IP vendor, or replacement for signal-integrity test equipment — this capability helps make high-speed link evidence decision-ready, traceable, and defensible for partners, labs, OEMs, boards, packages, and mission electronics programs."
  },
  {
    id: 28,
    title: "Total Solution Architecture 20-Slide Brief Now Available",
    date: "May 12, 2026",
    category: "Website Release / Page",
    image: "/img/salar_quadchart_fourlane.webp",
    imageAlt: "Four-lane assurance visual representing total solution architecture",
    link: "/total-solution-architecture",
    targetType: "page",
    targetUrl: "/total-solution-architecture",
    ctaLabel: "View Total Solution Architecture",
    badgeLabel: "Page",
    downloadUrl: "/documents/SALAR_Marketing_20Slide.pptx",
    downloadLabel: "Download 20-Slide PPTX",
    excerpt: "A new 20-slide deck gives visitors a 5–10 minute walkthrough of SALAR's Total Solution Architecture, including the Five-Pillar Health Model, scoring logic, risk drivers, and engagement path.",
    body: "SALAR Technologies LLC has added a concise 20-slide executive and technical brief for the Total Solution Architecture. The deck is designed for a fast 5–10 minute review and gives visitors a structured walkthrough of the Five-Pillar Health Model, scoring logic, confidence bands, risk drivers, mitigation workflow, and engagement path before they study the full architecture view."
  },
  {
    id: 27,
    title: "Total Solution Architecture Page Added",
    date: "May 10, 2026",
    category: "Website Release / Page",
    image: "/img/salar_whitepaper_divider.webp",
    imageAlt: "Abstract assurance architecture visual for total solution architecture",
    link: "/total-solution-architecture",
    targetType: "page",
    targetUrl: "/total-solution-architecture",
    ctaLabel: "View architecture page",
    badgeLabel: "Page",
    excerpt: "SALAR Technologies LLC added a Total Solution Architecture page showing how board evidence, BOM data, provenance signals, inspection results, risk heatmaps, trusted-zone graphs, and decision reports connect into one integrated assurance workflow.",
    body: "SALAR Technologies LLC added a Total Solution Architecture page showing how board evidence, BOM data, provenance signals, inspection results, risk heatmaps, trusted-zone graphs, and decision reports connect into one integrated assurance workflow. The new page documents the public-safe SALAR architecture: evidence inputs flow through normalization, a dependency graph, and risk scoring into decision outputs and trace reports. It also presents the P1\u2013P5 assurance model, an illustrative worked engineering example, the Observe\u2013Diagnose\u2013Decide\u2013Act repair loop, and the artifact set SALAR can produce. Public materials are informational and do not represent government certification, contract award, or endorsement."
  },
    {
    id: 26,
    title: "SALAR Resilience Terrain Explorer Public Demo Released",
    date: "May 3, 2026",
    category: "Website Release / Interactive Demo",
    image: "/img/salar_mission_continuity.webp",
    imageAlt: "Mission continuity visual representing the resilience terrain explorer",
    link: "/salar-resilience-terrain",
    targetType: "demo",
    targetUrl: "/salar-resilience-terrain",
    ctaLabel: "Open demo page",
    excerpt: "SALAR Technologies LLC released a standalone public webpage for the SALAR Resilience Terrain Explorer, an interactive demo for exploring resilience terrain, edge assurance, supply-chain risk signals, device-health indicators, and explainable decision support.",
    body: "SALAR Technologies LLC released a standalone public webpage for the SALAR Resilience Terrain Explorer, an interactive demo for exploring resilience terrain, edge assurance, supply-chain risk signals, device-health indicators, and explainable decision support. The public demo page now runs the SALAR Resilience Terrain Explorer locally inside the SALAR website, with embedded scenario controls, public-safe usage instructions, input/output guidance, and data-handling guardrails. The demo is designed for public-safe technical exploration using synthetic or sanitized examples. Users can test example scenarios involving edge telemetry, latency, device health, thermal stress, component provenance, supply-chain exposure, manufacturing drift, and operator decision support. Important notice: This demo is illustrative and public-safe. It is not a government system, not a customer deployment, not a certified product, and not intended for classified information, CUI, export-controlled technical data, proprietary BOMs, personal data, credentials, or operational government data."
  },
  {
    id: 25,
    title: "Public Compliance Statements Released",
    date: "May 2, 2026",
    category: "Compliance",
    image: "/img/about-salar-circuit-board.jpg",
    imageAlt: "Circuit-board and semiconductor systems representing engineering compliance assurance",
    link: "/compliance",
    targetType: "page",
    targetUrl: "/compliance",
    ctaLabel: "View compliance page",
    downloadUrl: "/pdf/SALAR_A1_Section_889_Compliance_Statement.pdf",
    downloadLabel: "Download Section 889 PDF",
    excerpt: "SALAR Technologies LLC published three public compliance-readiness statements covering Section 889, supply-chain compliance, and cybersecurity/federal safeguarding readiness.",
    body: "SALAR Technologies LLC published three public compliance-readiness statements covering Section 889, supply-chain compliance, and cybersecurity/federal safeguarding readiness. These documents are available on the Compliance page for federal, defense, prime-contractor, and partner review. They are public company self-attestations and do not represent government certification, contract award, or CMMC Level 2 certification."
  },
  {
    id: 20,
    title: "SALAR Launches AI Fabric Reliability & Infrastructure Assurance Capability",
    date: "April 30, 2026",
    category: "Capability Launch",
    image: "/img/ai_fabric_hero.jpg",
    imageAlt: "AI fabric infrastructure visual representing reliability assurance",
    link: "/ai-fabric-assurance",
    targetType: "page",
    targetUrl: "/ai-fabric-assurance",
    ctaLabel: "View AI Fabric page",
    downloadUrl: "/pdf/SALAR_AI_Fabric_Capability_Statement.pdf",
    downloadLabel: "Download capability statement",
    excerpt: "SALAR Technologies LLC has launched a dedicated AI Fabric Reliability & Infrastructure Assurance capability for organizations evaluating InfiniBand, Ethernet-for-AI, RoCE, NVIDIA Spectrum-X, and UEC-aligned architectures.",
    body: "SALAR Technologies LLC has launched a dedicated AI Fabric Reliability & Infrastructure Assurance capability, providing independent engineering decision support for organizations evaluating InfiniBand, Ethernet-for-AI, RoCE, NVIDIA Spectrum-X, and Ultra Ethernet Consortium (UEC)-aligned architectures. The new capability includes a 12-dimension fabric readiness scoring matrix, profile-based Ethernet governance methodology anchored to U.S. Patent Application No. 19/433,978, and a graph-based supply-chain dependency mapping framework from Patent Application No. 19/535,459. Target sectors include defense AI infrastructure, enterprise AI clusters, GPU cluster integrators, datacenter operators, and prime contractors requiring explainable, auditable network architecture decisions."
  },
  {
    id: 21,
    title: "Why AI Infrastructure Is Now a Network and Supply-Chain Decision",
    date: "April 2026",
    category: "Industry Focus",
    image: "/img/pexels_fiber_cables.jpg",
    imageAlt: "Fiber cables representing AI infrastructure network decisions",
    link: "/ai-fabric-assurance",
    targetType: "page",
    targetUrl: "/ai-fabric-assurance",
    ctaLabel: "View AI Fabric page",
    excerpt: "As AI clusters scale beyond 10,000 GPUs, the fabric choice between InfiniBand and Ethernet-for-AI becomes a critical architecture decision with long-term cost, reliability, and vendor-dependency consequences.",
    body: "As AI clusters scale beyond 10,000 GPUs, the fabric choice between InfiniBand and Ethernet-for-AI becomes a critical architecture decision with long-term cost, reliability, and vendor-dependency consequences. InfiniBand offers proven low-latency RDMA performance but creates single-vendor lock-in with a 2-3x per-port cost premium. Ethernet-for-AI offers multi-vendor flexibility but requires careful PFC/ECN/DCQCN configuration to achieve lossless behavior. SALAR Technologies addresses this gap with an independent assessment methodology that evaluates both paths against workload fit, operations maturity, supply-chain exposure, and sustainment requirements."
  },
  {
    title: "Eight-Patent Portfolio Addresses AI Infrastructure from Silicon to Supply Chain",
    date: "April 2026",
    category: "Patent Portfolio",
    image: "/img/circuit_chip.jpg",
    imageAlt: "Circuit chip visual representing the patent portfolio",
    link: "/patent-portfolio",
    targetType: "page",
    targetUrl: "/patent-portfolio",
    ctaLabel: "View patent portfolio",
    downloadUrl: "/pdf/SALAR_P2_Patent_Brief.pdf",
    downloadLabel: "Download P2 patent brief",
    excerpt: "April 2026 snapshot: SALAR's initial eight USPTO patent filings span board-level reliability, SoC connectivity, supply-chain risk, manufacturing stability, AI infrastructure, provenance, and agentic decision governance. The current ten-filing portfolio also includes high-speed validation and fault-tolerant quantum hardware assurance.",
    body: "This April 2026 announcement recorded SALAR Technologies' initial eight USPTO patent filings: board-level reliability governance (P1: 18/311,244), profile-based Ethernet connectivity for multi-lane SoC devices (P2: 19/433,978), supply-chain risk graph intelligence (P3: 19/535,459), closed-loop manufacturing stability (P4: 19/534,285), AI-certified lithography/process assurance (P5: 19/564,196), reliability-aware power/thermal orchestration for high-density AI computing infrastructure (P6: 19/685,128), hardware-anchored cryptographic provenance bridging for post-quantum verifiable attestation (P7: 19/686,634), and agentic action settlement governance (P8: 64/085,045). The current portfolio page also describes the later high-speed validation alignment and fault-tolerant quantum hardware assurance filings (P9 and P10)."
  },
  {
    id: 23,
    title: "Five Industries That Need Independent AI Fabric Assurance",
    date: "April 2026",
    category: "Target Industries",
    image: "/img/world_map.jpg",
    imageAlt: "Global infrastructure map representing AI fabric target industries",
    link: "/ai-fabric-assurance",
    excerpt: "SALAR Technologies has identified five primary sectors where independent AI fabric readiness assessments deliver the most value: defense AI infrastructure, enterprise AI clusters, GPU cluster integrators, datacenter operators, and prime contractors.",
    body: "SALAR Technologies has identified five primary sectors where independent AI fabric readiness assessments deliver the most value. (1) Defense AI Infrastructure — mission-critical clusters requiring auditable network decisions, supply-chain provenance, and CMMC-aligned documentation. (2) Enterprise AI Clusters — organizations choosing between dedicated InfiniBand fabrics and open Ethernet-for-AI architectures. (3) GPU Cluster Integrators — companies building AI systems needing independent validation of fabric architecture and supply-chain risk. (4) Datacenter Operators — teams managing multi-tenant AI environments needing congestion control and optics reliability review. (5) Prime Contractors and R&D Teams — government programs requiring explainable technical decision support for AI compute and networking procurement."
  },
  {
    id: 24,
    title: "SALAR Technologies LLC Launches Mission Assurance Capability",
    date: "March 2026",
    category: "Company",
    image: "/img/server_room_close.jpg",
    imageAlt: "Server room close-up representing mission assurance capability",
    link: "/mission-assurance",
    excerpt: "SALAR Technologies LLC has established its Mission Assurance capability focused on electronics reliability, semiconductor supply-chain intelligence, provenance and counterfeit-risk control, manufacturing stability, and predictive failure analytics.",
    body: "SALAR Technologies LLC has established its Mission Assurance capability focused on electronics reliability, semiconductor supply-chain intelligence, provenance and counterfeit-risk control, manufacturing stability, and predictive failure analytics for defense and government programs. The capability builds on founder Vikrant Salar's 21 years of engineering experience across SoC/ASIC design, hardware reliability, supply chain risk analytics, and advanced semiconductor manufacturing."
  },
  {
    id: 12,
    title: "Defensive IP Posture — LOT Network Membership Announced",
    date: "April 29, 2026",
    category: "Corporate Governance",
    excerpt: "SALAR Technologies LLC is a LOT Network member as part of its defensive intellectual-property risk posture. Membership reduces exposure to patent assertion entities (\"patent trolls\") and supports responsible patent ownership, partner confidence, and long-term innovation protection. Maintained alongside CMMC readiness, SAM.gov registration, and federal acquisition requirements as part of broader corporate governance and IP hygiene."
  },
  {
    id: 13,
    title: "UAE Public-Sector Opportunity Tracking — AI Video Analytics & Edge-IoT Visual Inspection",
    date: "April 29, 2026",
    category: "International Procurement",
    excerpt: "SALAR is tracking UAE public-sector interest in AI video analytics, edge computing, and IoT-based visual inspection systems for food safety, agriculture, industrial inspection, and smart-infrastructure environments. Bilingual EN/AR card published on the International Procurement page. Potential SALAR contribution: pilot-ready edge-AI visual inspection decision layer."
  },
  {
    id: 14,
    title: "European Deep-Tech Pipeline — imec.istart 2026 Spring Application Submitted",
    date: "April 29, 2026",
    category: "International Procurement",
    excerpt: "SALAR has submitted an application to the imec.istart.nl 2026 Spring call as part of its European deep-tech market-entry pathway. Dutch preselection outcome expected by mid-May 2026. German imec.istart application tenant access also enabled. Application is under review — no acceptance, funding, investment, partnership, endorsement, or program admission claimed at this stage."
  },
  {
    id: 15,
    title: "Site-Wide Improvements — Sticky Navigation & Per-Page SEO",
    date: "April 29, 2026",
    category: "Site Updates",
    excerpt: "Header navigation now stays pinned while scrolling on every page — no more scrolling back to the top to switch sections. Added per-page SEO metadata (title, description, Open Graph) across all 11 pages so LinkedIn, Twitter, and email link previews show page-specific content rather than a generic homepage card."
  },
  {
    id: 1,
    title: "SALAR Mission Thread™ Launched — CAD-PDM-CAM Engineering Assurance",
    date: "April 29, 2026",
    category: "Capability Launch",
    excerpt: "SALAR's flagship engineering-assurance methodology now public. Mission Thread converts CAD/ECAD, BOM, PDM/PLM, supplier, CAM, and test evidence into concise decision files for release readiness, source confidence, manufacturing readiness, and sustainment. Ten public-safe capability packets released for prime contractors, EMS, PCB design houses, defense suppliers, and DLA sustainment teams."
  },
  {
    id: 2,
    title: "International Procurement page promoted to top navigation",
    date: "April 25, 2026",
    category: "Site Updates",
    excerpt: "International Procurement Registrations now has its own top-nav tab. Active across UN, EU, ADB, GeBIZ Singapore, Qatar Innolight, UAE Ministry of Finance Digital Procurement, and other multilateral platforms — covering 5 continents."
  },
  {
    id: 3,
    title: "Compliance & Certifications page added — federal-contracting readiness",
    date: "April 24, 2026",
    category: "Compliance",
    excerpt: "New /compliance section consolidates SALAR's federal-contracting compliance posture: CMMC Level 1 FCI Self-Assessment, FAR 52.204-21 Cybersecurity Readiness, AI Use & Engineering Governance Addendum, Prime/Subcontractor Target Pack, and the master Compliance & Certification Stack. All artifacts public-safe and prime-contractor ready."
  },
  {
    id: 4,
    title: "Resilience Series launched — Continuity Under Stress & The Resilience Atlas",
    date: "April 24, 2026",
    category: "Publications",
    excerpt: "Two new long-form research volumes added to the SALAR publication library. Continuity Under Stress (Volume I) covers operational resilience for defense electronics programs under simultaneous shocks. The Resilience Atlas (Volume II) charts the geographic, technical, and strategic risk layers of U.S. defense electronics. Public 12-page summary editions available; full 600+ page editions available by request."
  },
  {
    id: 5,
    title: "Capability Presentation released for government & prime-contractor briefings",
    date: "April 24, 2026",
    category: "Government & Defense",
    excerpt: "Public, unclassified capability presentation now downloadable from the Government & Defense page. Summarizes SALAR's eight patent-backed capability lanes, registration credentials, and alignment with DoD / DLA / DARPA / DIU / USSOCOM mission priorities."
  },
  {
    id: 6,
    title: "U.S. defense agency logos linked to official .gov / .mil sites",
    date: "April 23, 2026",
    category: "Site Updates",
    excerpt: "DoD, DLA, DARPA, USSOCOM, DIU, and NSTXL logos on the Government & Defense page now click through to the official agency websites. Improves trust signals and supports contracting-officer due-diligence workflows."
  },
  {
    id: 7,
    title: "SBIR Performance Benchmark Applicability Report published",
    date: "April 18, 2026",
    category: "Publications",
    excerpt: "Applicability analysis mapping SALAR's patent-backed capability lanes against SBIR / STTR performance benchmarks across reliability, supply-chain analytics, manufacturing stability, and process assurance. Available on the Resources page."
  },
  {
    id: 8,
    title: "Public Patent Portfolio Overview published",
    date: "March 2026",
    category: "Innovation",
    excerpt: "March 2026 overview of SALAR's initial eight USPTO patent filings: board design reliability, multi-lane SoC connectivity, multi-tier supply-chain risk scoring with closed-loop mitigation, manufacturing stability, AI-certified lithography control, reliability-aware AI infrastructure power/thermal orchestration, quantum-safe provenance / cryptographic provenance bridging, and agentic action settlement governance. The current portfolio has since added high-speed validation alignment and fault-tolerant quantum hardware assurance (P9 and P10)."
  },
  {
    id: 9,
    title: "Government and Partnership Positioning Established",
    date: "March 2026",
    category: "Public Announcements",
    excerpt: "SALAR positioned for defense-adjacent, strategic-industry technical engagement and collaboration. SAM.gov registration active; UEI U4TVNBSBUJP3, CAGE 1AXR9 assigned."
  },
  {
    id: 10,
    title: "Patent Portfolio Development Underway",
    date: "March 2026",
    category: "Research Notes",
    excerpt: "Advancing public-safe patent portfolio across reliability, supply-chain intelligence, and manufacturing stability domains."
  },
  {
    id: 11,
    title: "SALAR Technologies LLC Formation Complete",
    date: "March 9, 2026",
    category: "Company Updates",
    excerpt: "SALAR Technologies LLC officially formed as Oregon LLC with comprehensive technical capability platform."
  }
];

export const companyInfo = {
  name: "SALAR Technologies LLC",
  tagline: "Semiconductor Supply Chain Analytics & Reliability Engineering",
  subtagline: "American Grit. Advanced Systems.",
  registryNumber: "254232599",
  state: "Oregon",
  formed: "March 9, 2026",
  location: "Wilsonville, Oregon, USA",
  phone: "408-601-7755",
  emails: {
    general: "contact@salartechnologies.com",
    direct: "vikrant@salartechnologies.com"
  },
  domain: "salartechnologies.com"
};

export const bookSeries = [
  {
    id: 1,
    title: "The American Arsenal",
    subtitle: "Defense Electronics Supply-Chain Sovereignty",
    seriesNumber: "Book 6",
    description: "Comprehensive integration of defense electronics supply chain challenges spanning counterfeit risk, rare earth dependencies, manufacturing resilience, and policy coordination. Synthesizes mitigation strategies, cost-benefit frameworks, and strategic recommendations for U.S. defense industrial base sovereignty.",
    pdfUrl: null,
    coverColor: "from-blue-600 to-blue-800",
    available: false
  },
  {
    id: 2,
    title: "The Rare Earth Trap",
    subtitle: "Critical Mineral Dependencies",
    tagline: "Where 80% of defense-grade rare earth processing runs through a single country, every weapons platform carries the same vulnerability.",
    seriesNumber: "Book 1",
    description: "Analyzes rare earth element dependencies in defense electronics platforms, identifying concentration risks in permanent magnets, precision-guided munitions, and sensor systems. Maps alternative sourcing pathways and strategic stockpiling frameworks for critical mineral supply chains.",
    pdfUrl: "/SALAR_Book1_The_Rare_Earth_Trap.pdf",
    coverColor: "from-cyan-600 to-blue-700",
    available: true
  },
  {
    id: 3,
    title: "The Counterfeit Menace",
    subtitle: "Electronics Authenticity & Detection",
    tagline: "Counterfeit components enter defense supply chains through secondary markets, obsolescence pressure, and compromised documentation. Detection requires more than visual inspection.",
    seriesNumber: "Book 2",
    description: "Examines counterfeit electronics infiltration vectors, detection methodologies including X-ray, decapsulation, and electrical fingerprinting. Provides risk mitigation frameworks for authorized-source verification and supplier authentication across defense supply chains.",
    pdfUrl: "/SALAR_Book2_The_Counterfeit_Menace.pdf",
    coverColor: "from-red-600 to-orange-700",
    available: true
  },
  {
    id: 4,
    title: "Manufacturing Sovereignty",
    subtitle: "Process Control & Production Independence",
    tagline: "When fabrication moves offshore, process knowledge follows. Rebuilding capacity is a decade-long effort. Losing it takes one procurement cycle.",
    seriesNumber: "Book 3",
    description: "Investigates nearshoring, reshoring, and on-shoring dependencies for defense electronics manufacturing. Analyzes process control stability, yield management, and production resilience strategies for mission-critical component fabrication and assembly operations.",
    pdfUrl: "/SALAR_Book3_Manufacturing_Sovereignty.pdf",
    coverColor: "from-green-600 to-emerald-700",
    available: true
  },
  {
    id: 5,
    title: "The Invisible Supply Chain",
    subtitle: "Hidden Dependencies & Risk Exposure",
    tagline: "Most defense programs see their Tier-1 suppliers. Less than 15% have visibility beyond Tier-2. The real risks hide in the layers no one maps.",
    seriesNumber: "Book 4",
    description: "Maps multi-tier supply chain opacity including sub-tier suppliers, broker networks, and unauthorized component substitution pathways. Quantifies hidden dependency exposure and establishes visibility frameworks for defense procurement transparency.",
    pdfUrl: "/SALAR_Book4_The_Invisible_Supply_Chain.pdf",
    coverColor: "from-indigo-600 to-blue-700",
    available: true
  },
  {
    id: 6,
    title: "Silicon Governance",
    subtitle: "Semiconductor Policy & Strategic Control",
    tagline: "Export controls, investment screening, and fabrication incentives now determine which chips defense systems access and when.",
    seriesNumber: "Book 5",
    description: "In-depth analysis of semiconductor governance, strategic control mechanisms, and policy frameworks for defense electronics.",
    shortBrief: "https://customer-assets.emergentagent.com/job_salar-web-app/artifacts/bhs1khn8_SALAR_book5_silicon_governance_Short_Brief.pdf",
    longBrief: "https://customer-assets.emergentagent.com/job_salar-web-app/artifacts/n3zhpvf0_SALAR_book5_silicon_governance_Long_Brief.pdf",
    coverColor: "from-purple-600 to-violet-700",
    available: true
  },
  {
    id: 7,
    title: "The American Arsenal",
    subtitle: "Defense Electronics Supply-Chain Sovereignty",
    tagline: "Five risk domains. One integrated assessment. The master compendium on U.S. defense electronics supply-chain sovereignty.",
    seriesNumber: "Book 6",
    description: "Comprehensive integration of defense electronics supply chain challenges spanning counterfeit risk, rare earth dependencies, manufacturing resilience, and policy coordination. Synthesizes mitigation strategies, cost-benefit frameworks, and strategic recommendations for U.S. defense industrial base sovereignty.",
    shortBrief: null,
    longBrief: null,
    coverColor: "from-blue-600 to-cyan-700",
    available: true
  },
  {
    id: 8,
    title: "Continuity Under Stress",
    subtitle: "Operational Resilience for Defense Electronics Programs",
    tagline: "When supply chains, fabrication lines, and component pipelines come under stress simultaneously, only programs designed for continuity survive without mission impact.",
    seriesNumber: "Volume I — Resilience Series",
    description: "A comprehensive treatment of how defense electronics programs sustain mission output when their underlying supply chains, manufacturing partners, and technology pipelines are stressed by geopolitical, economic, environmental, or adversarial pressure. Continuity Under Stress maps the failure modes that compound during simultaneous shocks — Tier-2/3 supplier loss, fabrication disruption, packaging-substrate scarcity, transportation interdiction, and IP/process leakage — and presents engineering-grade resilience frameworks for absorbing them. Drawing on case studies in semiconductor reshoring, rare-earth substitution, packaging-line redundancy, and counterfeit avoidance, the volume gives program offices, prime contractors, and DoD sustainment teams a working playbook for designing electronics programs that bend without breaking. The full edition runs 600+ pages with detailed engineering annexes, supplier-tier risk matrices, and program-office decision aids; the public summary distills the core argument and reference frameworks into a 12-page brief.",
    pdfUrl: "/SALAR_Continuity_Under_Stress_Summary.pdf",
    summaryNote: "12-page public summary edition. Full 600+ page reference edition available by request through partnership, collaboration, or government engagement channels.",
    coverColor: "from-amber-600 to-red-700",
    available: true,
    fullEditionContact: true
  },
  {
    id: 9,
    title: "The Resilience Atlas",
    subtitle: "A Geographic, Technical & Strategic Map of Defense Electronics Risk",
    tagline: "Most resilience strategies fail because the map is incomplete. The Atlas charts every layer — geographic, technical, and strategic — where defense electronics risk actually lives.",
    seriesNumber: "Volume II — Resilience Series",
    description: "The Resilience Atlas is a structured, multi-layer reference work charting where risk to U.S. defense electronics actually originates and propagates — across geographies, fabrication nodes, materials, packaging substrates, transportation corridors, regulatory regimes, and supplier ecosystems. Each chapter overlays a different risk dimension onto the same defense-electronics baseline, producing a composite atlas that program offices, prime contractors, and policy advisors can use to identify concentration exposure, single-source vulnerabilities, governance gaps, and mitigation opportunities at every tier of the supply chain. The Atlas pairs technical depth with strategic framing: it reads as both an engineering reference and a planning instrument for sustained mission readiness. The full edition runs 600+ pages with maps, tier matrices, and program-by-program risk overlays; the public summary distills the methodology and the eight master risk layers into a 12-page brief.",
    pdfUrl: "/SALAR_The_Resilience_Atlas_Summary.pdf",
    summaryNote: "12-page public summary edition. Full 600+ page reference edition available by request through partnership, collaboration, or government engagement channels.",
    coverColor: "from-emerald-600 to-teal-700",
    available: true,
    fullEditionContact: true
  }
];

export const videoContent = {
  introVideo: {
    title: "SALAR Technologies Introduction",
    description: "Professional introduction to SALAR Technologies LLC - engineering research for mission-grade reliability.",
    driveId: "1EIKnHEKYJ_-UU9cvGE1gnQkFIRe3EV_R",
    embedUrl: "https://drive.google.com/file/d/1EIKnHEKYJ_-UU9cvGE1gnQkFIRe3EV_R/preview"
  }
};

export const governmentRegistrations = {
  primary: {
    uei: "U4TVNBSBUJP3",
    cageCode: "1AXR9",
    duns: "144971571",
    samStatus: "Active",
    samExpiration: "March 26, 2027",
    purposeOfRegistration: "All Awards"
  },
  naicsCodes: [
    { code: "541330", description: "Engineering Services", primary: true },
    { code: "541511", description: "Custom Computer Programming Services", primary: false },
    { code: "541512", description: "Computer Systems Design Services", primary: false },
    { code: "541715", description: "Research and Development in the Physical, Engineering, and Life Sciences", primary: false }
  ],
  internationalRegistrations: [
    {
      name: "SAM.gov",
      status: "Active",
      url: "https://sam.gov",
      description: "U.S. Government procurement system"
    },
    {
      name: "UN Global Marketplace (UNGM)",
      status: "Active supplier account",
      url: "https://www.ungm.org",
      description: "United Nations procurement platform"
    },
    {
      name: "GeBIZ Singapore",
      status: "Trading Partner No. 57602119945",
      url: "https://www.gebiz.gov.sg",
      description: "Singapore government e-procurement"
    },
    {
      name: "EU Funding & Tenders Portal",
      status: "PIC No. 865203626",
      url: "https://ec.europa.eu/info/funding-tenders/opportunities/portal",
      description: "European Union funding opportunities"
    },
    {
      name: "UN UNICC In-Tend",
      status: "Ref. UNICC/RFP/2026/00222",
      url: "https://ungm.in-tend.co.uk/unicc/aspx/Home",
      description: "UN procurement system"
    },
    {
      name: "Asian Development Bank CMS",
      status: "No. 278192",
      url: "https://cms.adb.org",
      description: "ADB Consultant Management System"
    },
    {
      name: "UNDP Procurement",
      status: "Ref. UNDP-LBN-01371",
      url: "https://procurement-notices.undp.org",
      description: "UNDP procurement platform"
    },
    {
      name: "Qatar Open Innovation",
      status: "Aqua-Intelligence challenge",
      url: "https://innolight.qrdi.org.qa/en/innovation/p",
      description: "Qatar innovation platform"
    },
    {
      name: "UAE Ministry of Finance",
      status: "Ref. SUP130666",
      url: "https://mof.gov.ae/en/public-finance/government-procurement/digital-procurement-platform/",
      description: "UAE Digital Procurement Platform"
    }
  ],
  adbProjects: [
    { section: "222555", ta: "TA-10580", project: "50183-001" },
    { section: "222568", ta: "TA-10770", project: "56267-004" }
  ]
};

export const inquiryCategories = [
  "General Inquiry",
  "Engineering Project / Assessment Request",
  "RFI / RFQ / RFP / Solicitation",
  "Government / Defense Inquiry",
  "International / Public Procurement",
  "Semiconductor Supply-Chain / Provenance",
  "Electronics Reliability / Failure Analysis",
  "AI Fabric / AI Infrastructure Assurance",
  "Technical Due Diligence",
  "AWS Marketplace / Thermal Resilience Assessment Inquiry",
  "Partnership / Collaboration",
  "Technical / Research Inquiry",
  "Consulting / Advisory Inquiry"
];

export const internationalRegistrations = [
  {
    id: 1,
    name: "UN Global Marketplace (UNGM)",
    status: "Active Supplier Account",
    url: "https://www.ungm.org",
    logo: "https://www.un.org/sites/un2.un.org/files/2021/03/un-logo.png",
    fallbackText: "UN"
  },
  {
    id: 2,
    name: "GeBIZ Singapore",
    status: "Trading Partner No. 57602119945",
    url: "https://www.gebiz.gov.sg",
    logo: "https://flagcdn.com/w320/sg.png",
    fallbackText: "SG"
  },
  {
    id: 3,
    name: "EU Funding & Tenders Participant Register",
    status: "PIC No. 865203626",
    url: "https://ec.europa.eu/info/funding-tenders/opportunities/portal",
    logo: "https://flagcdn.com/w320/eu.png",
    fallbackText: "EU"
  },
  {
    id: 4,
    name: "UN UNICC In-Tend",
    status: "Reference: UNICC/RFP/2026/00222",
    url: "https://ungm.in-tend.co.uk/unicc/aspx/Home",
    logo: "https://www.un.org/sites/un2.un.org/files/2021/03/un-logo.png",
    fallbackText: "UN"
  },
  {
    id: 5,
    name: "Asian Development Bank CMS",
    status: "CMS No. 278192",
    url: "https://cms.adb.org",
    logo: "https://www.adb.org/themes/custom/adb/logo.svg",
    fallbackText: "ADB"
  },
  {
    id: 6,
    name: "ADB EOI - Section 222555",
    status: "TA-10580 / Project 50183-001",
    url: "https://cms.adb.org",
    logo: "https://www.adb.org/themes/custom/adb/logo.svg",
    fallbackText: "ADB"
  },
  {
    id: 7,
    name: "ADB EOI - Section 222568",
    status: "TA-10770 / Project 56267-004",
    url: "https://cms.adb.org",
    logo: "https://www.adb.org/themes/custom/adb/logo.svg",
    fallbackText: "ADB"
  },
  {
    id: 8,
    name: "UNDP Procurement",
    status: "Reference: UNDP-LBN-01371",
    url: "https://procurement-notices.undp.org",
    logo: "https://www.un.org/sites/un2.un.org/files/2021/03/un-logo.png",
    fallbackText: "UNDP"
  },
  {
    id: 9,
    name: "Qatar Open Innovation",
    status: "Aqua-Intelligence Challenge Submission",
    url: "https://innolight.qrdi.org.qa/en/innovation/p",
    logo: "https://flagcdn.com/w320/qa.png",
    fallbackText: "QA"
  },
  {
    id: 10,
    name: "UAE Ministry of Finance Digital Procurement",
    status: "Supplier Reference: SUP130666",
    url: "https://mof.gov.ae/en/public-finance/government-procurement/digital-procurement-platform/",
    logo: "https://flagcdn.com/w320/ae.png",
    fallbackText: "UAE"
  }
];





